import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowUpRight, ArrowDownRight, ArrowRightLeft, ListChecks } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { DataTable } from "../DataTable";
import { formatDate, formatAmount } from "../../lib/utils";
import { formatTransactionCategoryName } from "../../lib/category-relations";
import { getTransactionKind, getTransactionKindLabel, isTransferTransaction, TRANSACTION_KINDS } from "../../lib/transaction-types";

export function RecentTransactionsCard({ transactions, categories = [], privacyMode, isLoading, onAddTransaction }) {
  const [recentSort, setRecentSort] = useState("date-desc");

  const sortedRecentTransactions = useMemo(() => {
    const [sortField, sortOrder] = recentSort.split("-");
    const sorted = [...transactions].sort((a, b) => {
      if (sortField === "date") {
        return sortOrder === "desc" 
          ? new Date(b.date) - new Date(a.date)
          : new Date(a.date) - new Date(b.date);
      } else if (sortField === "amount") {
        return sortOrder === "desc"
          ? b.amount - a.amount
          : a.amount - b.amount;
      }
      return 0;
    });
    return sorted.slice(0, 5);
  }, [transactions, recentSort]);

  return (
    <Card className="card-hover">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Transações</CardTitle>
        <div className="flex items-center gap-2">
          <select 
            className="text-sm border border-input bg-background rounded-md px-2 py-1"
            value={recentSort}
            onChange={(e) => setRecentSort(e.target.value)}
          >
            <option value="date-desc">Data (Recente)</option>
            <option value="date-asc">Data (Antiga)</option>
            <option value="amount-desc">Maior Valor</option>
            <option value="amount-asc">Menor Valor</option>
          </select>
          <Link to="/transactions">
            <Button variant="ghost" size="sm">
              Ver todas
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <DataTable
            columns={[
              { key: "date", label: "Data", render: (row) => formatDate(row.date) },
              { key: "description", label: "Descrição", render: (row) => {
                const cleanDesc = row.description ? row.description.replace(/\s*\(\d+\/\d+\)$/, "") : "Sem descrição";
                const kind = getTransactionKind(row);
                const kindBadge = (kind === TRANSACTION_KINDS.TRANSFER || kind === TRANSACTION_KINDS.DETAILED) ? (
                  <span className="ml-0 mt-1 inline-flex w-fit items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary sm:ml-2 sm:mt-0">
                    {getTransactionKindLabel(kind)}
                  </span>
                ) : null;
                let badge = null;
                if (row.recurrence_type === "fixed") {
                  badge = <span className="ml-0 sm:ml-2 mt-1 sm:mt-0 w-fit text-[10px] uppercase font-bold tracking-wider bg-primary/10 text-primary px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 shrink-0" title="Fixa"><span className="text-[10px]">📌</span> FIXA</span>;
                } else if (row.recurrence_type === "installment" || row.recurrence_id) {
                  badge = <span className="ml-0 sm:ml-2 mt-1 sm:mt-0 w-fit text-[10px] uppercase font-bold tracking-wider bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 shrink-0" title="Repetição"><span className="text-[10px]">🔁</span> PARCELADO</span>;
                }
                const iconClass = kind === TRANSACTION_KINDS.TRANSFER
                  ? "bg-primary/10"
                  : row.type === "income" ? "bg-success/10" : "bg-destructive/10";
                return (
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-1.5 rounded-full shrink-0 ${iconClass}`}>
                      {kind === TRANSACTION_KINDS.TRANSFER ? (
                        <ArrowRightLeft className="w-3.5 h-3.5 text-primary" />
                      ) : kind === TRANSACTION_KINDS.DETAILED ? (
                        <ListChecks className="w-3.5 h-3.5 text-destructive" />
                      ) : row.type === "income" ? (
                        <ArrowUpRight className="w-3.5 h-3.5 text-success" />
                      ) : (
                        <ArrowDownRight className="w-3.5 h-3.5 text-destructive" />
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center min-w-0">
                      <span className="font-medium truncate max-w-[120px] sm:max-w-xs">{cleanDesc}</span>
                      {kindBadge}
                      {badge}
                    </div>
                  </div>
                );
              }},
              { key: "category", label: "Categoria", render: (row) => formatTransactionCategoryName(row, categories) },
              { key: "amount", label: "Valor", align: "right", render: (row) => (
                <span className={`font-mono font-semibold ${privacyMode ? "privacy-blur" : ""} ${isTransferTransaction(row) ? "text-primary" : row.type === "income" ? "text-success" : "text-destructive"}`}>
                  {formatAmount(row.amount)}
                </span>
              )}
            ]}
            data={sortedRecentTransactions}
            onRowClick={onAddTransaction}
            emptyState={
              <div className="empty-state py-8">
                <p className="text-muted-foreground mb-4">Nenhuma transação ainda</p>
                <Button onClick={onAddTransaction} data-testid="empty-add-transaction">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Transação
                </Button>
              </div>
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
